import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const SalaryAllocationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate(); 
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    userId: '',
    year: new Date().getFullYear().toString(),
    ctc: '',
    perMonthCtc: '',
    basic: '',
    da: '',
    hra: '',
    otherAllowance: '',
    specialAllowance: '',
    gross: '',
    pfEe: '1800',
    esiEe: '',
    pt: '208.33',
    tdsDeduction: '',
    otherDeduction: '',
    totalDeductionEe: '2008.33',
    net: '',
    pfEr: '',
    esiEr: '',
    basicPlusDa: '',
    gratuity: ''
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null); // Track which field is focused

  useEffect(() => {
    if (!id) {
      toast.error('Salary details identifier is missing.');
      navigate('/payroll-allocation');
      return;
    }
    fetchEmployees();
    fetchSalaryDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch employees list
  const fetchEmployees = async () => {
    if (!API_URL) return;

    setLoadingEmployees(true);
    try {
      const payload = {
        action: 'get-active-employees-list'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setEmployees(result.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch employees list');
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Fetch existing salary details
  const fetchSalaryDetails = async (salaryId) => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      setLoadingData(false);
      return;
    }

    const currentUserId = localStorage.getItem('id');
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      setLoadingData(false);
      navigate('/payroll-allocation');
      return;
    }

    setLoadingData(true);
    try {
      const payload = {
        action: 'get-salary-details',
        id: salaryId,
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.data) {
        const data = result.data;
        // Populate form with existing data
        setFormData({
          name: data.name || '',
          userId: data.userId || '',
          year: data.year ? data.year.toString() : new Date().getFullYear().toString(),
          ctc: data.ctc || '',
          perMonthCtc: data.perMonthCtc || '',
          basic: data.basic || '',
          da: data.da || '',
          hra: data.hra || '',
          otherAllowance: data.otherAllowance || '',
          specialAllowance: data.specialAllowance || '',
          gross: data.gross || '',
          pfEe: data.pfEe || '',
          esiEe: data.esiEe || '',
          pt: data.pt || '',
          tdsDeduction: data.tdsDeduction || '',
          otherDeduction: data.otherDeduction || '',
          totalDeductionEe: data.totalDeductionEe || '',
          net: data.net || '',
          pfEr: data.pfEr || '',
          esiEr: data.esiEr || '',
          basicPlusDa: data.basicPlusDa || '',
          gratuity: data.gratuity || ''
        });
      } else {
        toast.error(result?.message || 'Failed to fetch salary details');
        navigate('/payroll-allocation');
      }
    } catch (error) {
      console.error('Error fetching salary details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
      navigate('/payroll-allocation');
    } finally {
      setLoadingData(false);
    }
  };

  // Format number for display (with commas and 2 decimals)
  const formatNumberForDisplay = (value) => {
    if (!value || value === '') return '';
    const numValue = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Get display value - formatted if not focused, raw if focused
  const getDisplayValue = (name, value) => {
    const numericFields = [
      'ctc', 'perMonthCtc', 'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance', 'gross',
      'pfEe', 'esiEe', 'pt', 'tdsDeduction', 'otherDeduction', 'totalDeductionEe', 'net', 'pfEr', 'esiEr',
      'basicPlusDa', 'gratuity'
    ];
    
    if (numericFields.includes(name)) {
      // If field is focused, show raw value for easy editing
      if (focusedField === name) {
        return value || '';
      }
      // If not focused, show formatted value
      return formatNumberForDisplay(value);
    }
    return value || '';
  };

  const parseNumber = (value) => {
    return parseFloat((value || '').toString().replace(/,/g, '') || 0) || 0;
  };

  const applyAutoCalculations = (data) => {
    const basic = parseNumber(data.basic);
    const da = parseNumber(data.da);
    const basicPlusDa = basic + da;
    const hra = basicPlusDa > 0 ? (basicPlusDa * 0.5) : 0;
    const gratuity = basicPlusDa > 0 ? Math.round(basicPlusDa * 0.0481) : 0;

    data.basicPlusDa = basicPlusDa > 0 ? basicPlusDa.toFixed(2) : '';
    data.hra = basicPlusDa > 0 ? hra.toFixed(2) : '';
    data.gratuity = basicPlusDa > 0 ? gratuity.toFixed(2) : '';

    const perMonthCtc = parseNumber(data.perMonthCtc);
    const pfEe = parseNumber(data.pfEe);
    const specialAllowance = parseNumber(data.specialAllowance);

    if (perMonthCtc > 0) {
      const autoOtherAllowance = Math.round(perMonthCtc - (basicPlusDa + hra + pfEe + gratuity + specialAllowance));
      data.otherAllowance = (autoOtherAllowance > 0 ? autoOtherAllowance : 0).toFixed(2);
    } else {
      data.otherAllowance = '';
    }

    const otherAllowance = parseNumber(data.otherAllowance);
    const gross = basic + da + hra + otherAllowance + specialAllowance;
    data.gross = gross > 0 ? gross.toFixed(2) : '';

    const esiEe = parseNumber(data.esiEe);
    const pt = parseNumber(data.pt);
    const tdsDeduction = parseNumber(data.tdsDeduction);
    const otherDeduction = parseNumber(data.otherDeduction);
    const totalDeduction = pfEe + esiEe + pt + tdsDeduction + otherDeduction;
    data.totalDeductionEe = totalDeduction > 0 ? totalDeduction.toFixed(2) : '';
    data.net = (gross - totalDeduction) > 0 ? (gross - totalDeduction).toFixed(2) : '';

    return data;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let validatedValue = value;

    const numericFields = [
      'ctc', 'perMonthCtc', 'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance', 'gross',
      'pfEe', 'esiEe', 'pt', 'tdsDeduction', 'otherDeduction', 'totalDeductionEe', 'net', 'pfEr', 'esiEr',
      'basicPlusDa', 'gratuity'
    ];

    if (numericFields.includes(name)) {
      let cleaned = value.replace(/,/g, '');
      cleaned = cleaned.replace(/[^\d.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      validatedValue = cleaned;
    }

    setFormData(prev => ({
      ...prev,
      [name]: validatedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    setTimeout(() => {
      setFormData(prev => {
        const updatedData = { ...prev, [name]: validatedValue };

        if (name === 'ctc') {
          const ctcValue = parseFloat(validatedValue.replace(/,/g, '') || 0);
          if (!isNaN(ctcValue) && ctcValue > 0) {
            const perMonthCtc = ctcValue / 12;
            const defaultDa = 7500;
            const calculatedBasic = (perMonthCtc * 0.5) - defaultDa;

            updatedData.perMonthCtc = perMonthCtc.toFixed(2);
            updatedData.da = defaultDa.toFixed(2);
            updatedData.basic = calculatedBasic > 0 ? calculatedBasic.toFixed(2) : '0.00';
          } else {
            updatedData.perMonthCtc = '';
            updatedData.da = '';
            updatedData.basic = '';
          }
        }

        return applyAutoCalculations(updatedData);
      });
    }, 0);
  };

  const handleEmployeeChange = (e) => {
    const selectedUserId = e.target.value;
    const selectedEmployee = employees.find(emp => emp.userId === selectedUserId);
    
    setFormData(prev => ({
      ...prev,
      userId: selectedUserId,
      name: selectedEmployee?.fullName || ''
    }));

    if (errors.userId) {
      setErrors(prev => ({
        ...prev,
        userId: ''
      }));
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    setFormData(prev => ({
      ...prev,
      year: year
    }));

    if (errors.year) {
      setErrors(prev => ({
        ...prev,
        year: ''
      }));
    }
  };

  // Generate years array (from current year - 10 to current year + 5)
  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 10;
    const endYear = currentYear + 5;
    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    setFocusedField(name);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFocusedField(null);
    
    // Format the value on blur
    const numericFields = [
      'ctc', 'perMonthCtc', 'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance', 'gross',
      'pfEe', 'esiEe', 'pt', 'tdsDeduction', 'otherDeduction', 'totalDeductionEe', 'net', 'pfEr', 'esiEr',
      'basicPlusDa', 'gratuity'
    ];
    
    if (numericFields.includes(name)) {
      const value = formData[name];
      if (value && value !== '') {
        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        if (!isNaN(numValue)) {
          // Store the numeric value (without formatting) for calculations
          setFormData(prev => ({
            ...prev,
            [name]: numValue.toFixed(2)
          }));
        }
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userId) {
      newErrors.userId = 'Employee is required';
    }
    if (!formData.year || formData.year.length !== 4) {
      newErrors.year = 'Valid year is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload - send empty string for empty fields to allow clearing
      // Remove commas from numeric values before sending to API
      const cleanNumericValue = (val) => {
        if (!val || val === '') return '';
        const cleaned = val.toString().replace(/,/g, '');
        return cleaned || '';
      };

      // Get employee name from selected employee
      const selectedEmployee = employees.find(emp => emp.userId === formData.userId);
      const employeeName = selectedEmployee?.fullName || formData.name || '';

      const payload = {
        action: 'update-salary-details',
        id: id,
        currentUserId: currentUserId,
        name: employeeName.trim(),
        userId: formData.userId,
        year: parseInt(formData.year),
        // All fields - send empty string if empty to allow clearing
        ctc: cleanNumericValue(formData.ctc),
        perMonthCtc: cleanNumericValue(formData.perMonthCtc),
        basic: cleanNumericValue(formData.basic),
        da: cleanNumericValue(formData.da),
        hra: cleanNumericValue(formData.hra),
        otherAllowance: cleanNumericValue(formData.otherAllowance),
        specialAllowance: cleanNumericValue(formData.specialAllowance),
        gross: cleanNumericValue(formData.gross),
        pfEe: cleanNumericValue(formData.pfEe),
        esiEe: cleanNumericValue(formData.esiEe),
        pt: cleanNumericValue(formData.pt),
        tdsDeduction: cleanNumericValue(formData.tdsDeduction),
        otherDeduction: cleanNumericValue(formData.otherDeduction),
        totalDeductionEe: cleanNumericValue(formData.totalDeductionEe),
        net: cleanNumericValue(formData.net),
        pfEr: cleanNumericValue(formData.pfEr),
        esiEr: cleanNumericValue(formData.esiEr),
        basicPlusDa: cleanNumericValue(formData.basicPlusDa),
        gratuity: cleanNumericValue(formData.gratuity)
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result.message || 'Salary details updated successfully');
        setTimeout(() => {
          navigate('/payroll-allocation');
        }, 1500);
      } else {
        toast.error(result?.message || 'Failed to update salary details');
      }
    } catch (error) {
      console.error('Error updating salary details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get date for year picker (default to January 1st of selected year)

  if (loadingData) {
    return (
      <div className="employee-form">
        <section className="content">
          <div className="container-fluid">
            <div className="card employee-content-card">
              <div className="card-body">
                <Loading size={100} />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                Edit Salary Allocation
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Update salary allocation details for employees.
              </p>
            </div>
          </div>

          {/* Form Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              {loadingEmployees ? (
                <Loading size={80} />
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Required Fields Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Required Information</h4>
                    <p className="section-subtitle">
                      Fill in the required details below
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="userId" className="form-label">
                          Employee<span className="required-star">*</span>
                        </label>
                        <select
                          id="userId"
                          name="userId"
                          className={`form-control ${errors.userId ? 'is-invalid' : ''}`}
                          value={formData.userId}
                          onChange={handleEmployeeChange}
                        >
                          <option value="">Select Employee</option>
                          {employees.map(emp => (
                            <option key={emp.userId} value={emp.userId}>
                              {emp.fullName || '-'}
                            </option>
                          ))}
                        </select>
                        {errors.userId && (
                          <div className="invalid-feedback">{errors.userId}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="year" className="form-label">
                          Year<span className="required-star">*</span>
                        </label>
                        <div className="date-input-wrapper" style={{ position: 'relative' }}>
                          <select
                            id="year"
                            name="year"
                            className={`form-control ${errors.year ? 'is-invalid' : ''}`}
                            value={formData.year}
                            onChange={handleYearChange}
                            style={{
                              padding: '8px 35px 8px 12px',
                              fontSize: '14px',
                              height: '40px',
                              appearance: 'none',
                              backgroundImage: 'none'
                            }}
                          >
                            <option value="">Select Year</option>
                            {getYears().map(year => (
                              <option key={year} value={year.toString()}>
                                {year}
                              </option>
                            ))}
                          </select>
                          <i className="fas fa-calendar-alt date-icon" style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            pointerEvents: 'none',
                            fontSize: '14px'
                          }}></i>
                        </div>
                        {errors.year && (
                          <div className="invalid-feedback">{errors.year}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Salary Details Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Salary Details</h4>
                    <p className="section-subtitle">
                      Enter salary components and calculations (Optional)
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="ctc" className="form-label">CTC (Annual)</label>
                        <input
                          type="text"
                          id="ctc"
                          name="ctc"
                          className="form-control"
                          value={getDisplayValue('ctc', formData.ctc)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter CTC"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="perMonthCtc" className="form-label">Per Month CTC</label>
                        <input
                          type="text"
                          id="perMonthCtc"
                          name="perMonthCtc"
                          className="form-control"
                          value={getDisplayValue('perMonthCtc', formData.perMonthCtc)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="basic" className="form-label">Basic</label>
                        <input
                          type="text"
                          id="basic"
                          name="basic"
                          className="form-control"
                          value={getDisplayValue('basic', formData.basic)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter basic salary"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="da" className="form-label">DA (Dearness Allowance)</label>
                        <input
                          type="text"
                          id="da"
                          name="da"
                          className="form-control"
                          value={getDisplayValue('da', formData.da)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter DA"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="hra" className="form-label">HRA (House Rent Allowance)</label>
                        <input
                          type="text"
                          id="hra"
                          name="hra"
                          className="form-control"
                          value={getDisplayValue('hra', formData.hra)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="otherAllowance" className="form-label">Other Allowance</label>
                        <input
                          type="text"
                          id="otherAllowance"
                          name="otherAllowance"
                          className="form-control"
                          value={getDisplayValue('otherAllowance', formData.otherAllowance)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="specialAllowance" className="form-label">Special Allowance</label>
                        <input
                          type="text"
                          id="specialAllowance"
                          name="specialAllowance"
                          className="form-control"
                          value={getDisplayValue('specialAllowance', formData.specialAllowance)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter special allowance"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="gross" className="form-label">Gross Salary</label>
                        <input
                          type="text"
                          id="gross"
                          name="gross"
                          className="form-control"
                          value={getDisplayValue('gross', formData.gross)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="basicPlusDa" className="form-label">Basic + DA</label>
                        <input
                          type="text"
                          id="basicPlusDa"
                          name="basicPlusDa"
                          className="form-control"
                          value={getDisplayValue('basicPlusDa', formData.basicPlusDa)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Deductions Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Deductions (Employee)</h4>
                    <p className="section-subtitle">
                      Employee-side deductions
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="pfEe" className="form-label">PF (Employee)</label>
                        <input
                          type="text"
                          id="pfEe"
                          name="pfEe"
                          className="form-control"
                          value={getDisplayValue('pfEe', formData.pfEe)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter PF (Employee)"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="esiEe" className="form-label">ESI (Employee)</label>
                        <input
                          type="text"
                          id="esiEe"
                          name="esiEe"
                          className="form-control"
                          value={getDisplayValue('esiEe', formData.esiEe)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter ESI (Employee)"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="pt" className="form-label">PT (Professional Tax)</label>
                        <input
                          type="text"
                          id="pt"
                          name="pt"
                          className="form-control"
                          value={getDisplayValue('pt', formData.pt)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter PT"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="tdsDeduction" className="form-label">TDS Deduction</label>
                        <input
                          type="text"
                          id="tdsDeduction"
                          name="tdsDeduction"
                          className="form-control"
                          value={getDisplayValue('tdsDeduction', formData.tdsDeduction)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter TDS deduction"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="otherDeduction" className="form-label">Other Deduction</label>
                        <input
                          type="text"
                          id="otherDeduction"
                          name="otherDeduction"
                          className="form-control"
                          value={getDisplayValue('otherDeduction', formData.otherDeduction)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter other deduction"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="totalDeductionEe" className="form-label">Total Deduction (Employee)</label>
                        <input
                          type="text"
                          id="totalDeductionEe"
                          name="totalDeductionEe"
                          className="form-control"
                          value={getDisplayValue('totalDeductionEe', formData.totalDeductionEe)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="net" className="form-label">Net Salary</label>
                        <input
                          type="text"
                          id="net"
                          name="net"
                          className="form-control"
                          value={getDisplayValue('net', formData.net)}
                          placeholder="Auto calculated"
                          readOnly
                          style={{ fontWeight: '600', color: '#28a745' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Employer Contributions Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Employer Contributions</h4>
                    <p className="section-subtitle">
                      Employer-side contributions
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="pfEr" className="form-label">PF (Employer)</label>
                        <input
                          type="text"
                          id="pfEr"
                          name="pfEr"
                          className="form-control"
                          value={getDisplayValue('pfEr', formData.pfEr)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter PF (Employer)"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="esiEr" className="form-label">ESI (Employer)</label>
                        <input
                          type="text"
                          id="esiEr"
                          name="esiEr"
                          className="form-control"
                          value={getDisplayValue('esiEr', formData.esiEr)}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          placeholder="Enter ESI (Employer)"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="gratuity" className="form-label">Gratuity</label>
                        <input
                          type="text"
                          id="gratuity"
                          name="gratuity"
                          className="form-control"
                          value={getDisplayValue('gratuity', formData.gratuity)}
                          placeholder="Auto calculated"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate('/payroll-allocation')}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span>Updating...</span>
                        </>
                      ) : (
                        <>
                          <span>Update Salary Allocation</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalaryAllocationEdit;
